import { useId, useState } from "react";
import { Plus, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { whatsappLink, type Service } from "@/lib/site";

type ServiceCardProps = {
  service: Service;
  index: number;
  defaultOpen?: boolean;
};

export function ServiceCard({ service, index, defaultOpen = false }: ServiceCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div
      className={cn(
        "group border-t transition-colors duration-500",
        open ? "border-gold/50" : "border-border hover:border-gold/40",
      )}
    >
      <h3>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex w-full cursor-pointer items-center gap-5 py-7 text-left"
        >
          <span className="font-display text-sm text-gold">0{index + 1}</span>

          <span className="flex-1">
            <span
              className={cn(
                "block font-display text-xl transition-colors duration-500 sm:text-2xl",
                open ? "text-gold" : "group-hover:text-gold",
              )}
            >
              {service.name}
            </span>
            <span className="mt-1.5 flex items-center gap-2 font-sans text-[0.625rem] tracking-[0.2em] text-muted-foreground uppercase">
              <Clock className="h-3 w-3" strokeWidth={1.5} />
              {service.duration}
            </span>
          </span>

          <span className="text-right">
            <span className="block font-display text-lg whitespace-nowrap sm:text-xl">
              {service.price}
            </span>
          </span>

          <span
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-500",
              open
                ? "rotate-45 border-gold bg-gold/10 text-gold"
                : "border-border text-muted-foreground group-hover:border-gold group-hover:text-gold",
            )}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          </span>
        </button>
      </h3>

      <div
        id={panelId}
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="pb-7 pl-[3.1rem]">
            <p className="max-w-md font-sans text-sm leading-relaxed font-light text-muted-foreground">
              {service.detail}
            </p>

            <ul className="mt-5 flex flex-wrap gap-2">
              {service.includes.map(item => (
                <li
                  key={item}
                  className="rounded-full border border-border bg-secondary/60 px-3 py-1.5 font-sans text-[0.625rem] tracking-[0.14em] text-muted-foreground uppercase"
                >
                  {item}
                </li>
              ))}
            </ul>

            <a
              href={whatsappLink(
                `Hi Aditi! I'd like to book the ${service.name} look (${service.price}).`,
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 font-sans text-[0.625rem] tracking-[0.22em] text-foreground uppercase link-underline"
            >
              Enquire about {service.name}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
